import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from "@angular/core";
import { Observable } from "rxjs";
import { SafeUrl } from "@angular/platform-browser";
import { MatSelectionList } from "@angular/material/list";
import moment from "moment";

@Component({
  selector: "app-manage-file-item",
  templateUrl: "./manage-file-item.component.html",
  styleUrls: ["./manage-file-item.component.scss"],
})
export class ManageFileItemComponent {
  @ViewChild("selectionList", { static: false })
  selectionList?: MatSelectionList;

  @Input() file: any;
  @Input() allowMultiple: boolean;
  @Input() selected = false;
  @Input() thumbnail$: Observable<SafeUrl> | null = null;
  @Input() fileOperationStatus: any;
  @Input() disableToggle = false;
  @Input() uploading = false;
  @Input() tenantDateTimeFormat: { dateTimeFormat: any; timezone: any };

  @Output() toggleSelection = new EventEmitter<{
    id: string;
    checked: boolean;
  }>();
  @Output() download = new EventEmitter<void>();
  @Output() remove = new EventEmitter<void>();
  @Output() preview = new EventEmitter<void>();

  public infoIsOpen = false;
  private hoverTimeout: any;

  public placeholderSrc = "assets/images/no-multimedia.png";

  enterTrigger() {
    clearTimeout(this.hoverTimeout);
    this.infoIsOpen = true;
  }

  leaveTrigger() {
    this.scheduleClose();
  }

  enterOverlay() {
    clearTimeout(this.hoverTimeout);
  }

  leaveOverlay() {
    this.scheduleClose();
  }

  scheduleClose() {
    this.hoverTimeout = setTimeout(() => {
      this.infoIsOpen = false;
    }, 200);
  }

  onPreview(e: MouseEvent) {
    e.stopPropagation();
    this.preview.emit();
  }

  onDownload(e: MouseEvent) {
    e.stopPropagation();
    this.download.emit();
  }

  onRemove(e: MouseEvent) {
    e.stopPropagation();
    this.remove.emit();
  }

  onThumbError(e: Event) {
    const img = e.target as HTMLImageElement;
    if (img && img.src !== this.placeholderSrc) {
      img.src = this.placeholderSrc;
    }
  }

  onToggleSelection(checked: boolean, e?: unknown) {
    if (e && typeof (e as any).stopPropagation === "function") {
      (e as Event).stopPropagation();
    }
    const id = this.file?.id ?? null;
    this.toggleSelection.emit({ id: id as any, checked });
  }

  onCardClick(e: MouseEvent) {
    const status = this.file?.fileStatus;
    const S = this.fileOperationStatus || {};
    if (this.uploading || this.disableToggle || status != S.CONVERSION_DONE) {
      e.stopPropagation();
      return;
    }
    this.onToggleSelection(!this.selected, e);
  }

  formatDateTime(date): string {
    return (this.tenantDateTimeFormat.timezone === "UTC"
        ? moment.utc(date)
        : moment(date))
      .format(this.tenantDateTimeFormat.dateTimeFormat)
  }

  get formattedUploadedOn(): string {
    return this.formatDateTime(this.file?.uploadedOn);
  }

  get formattedLastModificationOn(): string {
    return this.formatDateTime(this.file?.lastModificationOn);
  }

  get formattedLength(): string {
    if (!this.file.lengthInSeconds) {
      return "";
    }

    const m = Math.floor(this.file.lengthInSeconds / 60);
    const s = this.file.lengthInSeconds % 60;

    return m ? `${m}m ${s}s` : `${s}s`;
  }

  get statusLabel(): string {
    if (this.uploading) return "Uploading";
    const s = this.file?.fileStatus;
    const S = this.fileOperationStatus || {};
    if (s === S.CONVERSION_PENDING) return "conversionPending";
    if (s === S.CONVERTING) return "converting";
    if (s === S.WAITING_FOR_CONVERSION) return "waitingForConversion";
    if (s === S.CONVERSION_FAILED) return "conversionFailed";
    if (s === S.CONVERSION_DONE) return "conversionDone";
    return "";
  }

  get statusType(): "success" | "warning" | "info" | "error" {
    const s = this.file?.fileStatus;
    const S = this.fileOperationStatus || {};
    if (s === S.CONVERSION_DONE) return "success";
    if (s === S.CONVERSION_PENDING) return "warning";
    if (s === S.WAITING_FOR_CONVERSION) return "warning";
    if (s === S.CONVERTING) return "info";
    if (s === S.CONVERSION_FAILED) return "error";
    return "info";
  }

  get statusIcon(): string | null {
    switch (this.statusType) {
      case "success":
        return "check_circle";
      case "warning":
        return "warning";
      case "error":
        return "error";
      default:
        return "info";
    }
  }
}
